import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, timer } from 'rxjs';
import { MmuAnnouncement, MmuAnnouncementConfig } from '../models/announcement';


@Injectable({
  providedIn: 'root'
})
export class MmuAnnouncementService {
  _announcements$ = new BehaviorSubject<MmuAnnouncement[]>([]);
  private _announcements: MmuAnnouncement[] = [];
  readonly localStorageKey = 'announcementConfigs';

  get announcements(): Observable<MmuAnnouncement[]> {
    return this._announcements$.asObservable();
  }

  createAnnouncement(announcement: MmuAnnouncement): void {
    this._announcements.push(announcement);
    this.handleAnnouncement(announcement.config);
  }

  getStoredAnnouncementConfig(announcementId: string): MmuAnnouncementConfig | null {
    if (localStorage.getItem(this.localStorageKey)) {
      const storedAnnouncementConfigs = JSON.parse(localStorage.getItem(this.localStorageKey) ?? '');
      return storedAnnouncementConfigs.find((config: MmuAnnouncementConfig) => config.id === announcementId);
    }

    return null;
  }

  updateConfig(config: MmuAnnouncementConfig): void {
    if (!this._announcements) {
      return;
    }

    const announcement = this._announcements.find(a => a.config.id === config.id);

    if (announcement) {
      announcement.config = config;
    }

    const storedAnnouncementConfigs = JSON.parse(localStorage.getItem(this.localStorageKey) ?? '[]');
    const storedConfig = storedAnnouncementConfigs.find((c: MmuAnnouncementConfig) => c.id === config.id);

    if (storedConfig) {
      Object.assign(storedConfig, config);
    } else {
      storedAnnouncementConfigs.push(config);
    }

    localStorage.setItem(this.localStorageKey, JSON.stringify(storedAnnouncementConfigs));
    this._announcements$.next(this._announcements);
  }

  private handleAnnouncement(config: MmuAnnouncementConfig): void {
    if (!config) {
      return;
    }

    const storedConfig = this.getStoredAnnouncementConfig(config.id);
    if (storedConfig && this._announcements.length > 0) {

      const announcement = this._announcements.find(a => a.config.id === config.id);
      if (announcement) {
        announcement.config = storedConfig;
      }

      config = { ...storedConfig, ...config };
      if (config.display) {
        this.showBanner(config);
        return;
      }
    }

    if (config.lastDismissedAt && this.dismissedInLastDay(new Date(config.lastDismissedAt))) {
      // already dismissed in last day
      return;
    }

    if (config.dismissLimit && config.dismissCount && config.dismissCount >= config.dismissLimit) {
      // already dismissed until the limit
      return;
    }

    if (!config.display) {
      if (config.delay) {
        //display after a delay
        timer(config.delay).subscribe(() => {
          this.showBanner(config);
        });
      } else {
        //display immediately
        this.showBanner(config);
      }
    } else {
      this.showBanner(config);
    }
  }

  private showBanner(config: MmuAnnouncementConfig): void {
    config.display = true;
    this.updateConfig(config);
  }

  dismissedInLastDay(startDate: Date): boolean {
    const timeDifference = Math.abs(new Date().getTime() - startDate.getTime());
    const twentyFourHoursInMilliseconds = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

    return timeDifference < twentyFourHoursInMilliseconds;
  }

  removeBanner(bannerId: string): void {
    this._announcements = this._announcements.filter(a => a.config.id !== bannerId);
    this._announcements$.next(this._announcements);

    const storedConfigs = localStorage.getItem(this.localStorageKey);
    if (storedConfigs) {
      const storedAnnouncementConfigs = JSON.parse(storedConfigs);
      const updatedConfigs = storedAnnouncementConfigs.filter((config: MmuAnnouncementConfig) => config.id !== bannerId);
      localStorage.setItem(this.localStorageKey, JSON.stringify(updatedConfigs));
    }
  }

  dismissAnnouncement(announcementId: string): void {
    const storedConfig = this.getStoredAnnouncementConfig(announcementId);

    if (storedConfig) {
      storedConfig.display = false;
      if (!storedConfig.dismissCount) {
        storedConfig.dismissCount = 0;
      }
      storedConfig.dismissCount++;
      storedConfig.lastDismissedAt = new Date();

      this.updateConfig(storedConfig);
    }
  }
}
